import { useEffect } from 'react'
import { useTheme } from '@chakra-ui/react'
import { useRouterChanged } from 'ui/hooks'
import { type AihubTabId } from 'ecosystem'
import { createUrl } from 'shared-utils'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useAihubState } from './useAihubState'
import { useAihubNotifications } from './useAihubNotifications'
import { type AihubVisibility } from '../utils'

type UseAihubProps = {
  offers?: {
    isEnabled?: boolean
  }
  visibility?: AihubVisibility
}

export const useAihub = (props?: UseAihubProps) => {
  const { isOpen, closeAihub, openAihub, setActiveTabId } = useAihubState()
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const theme = useTheme()
  const tabletBreakpoint: string | undefined = theme.breakpoints?.sm
  const desktopBreakpoint: string | undefined = theme.breakpoints?.md
  const showOnDesktop = props?.visibility?.showOnDesktop ?? true
  const showOnTablet = props?.visibility?.showOnTablet ?? true
  const showOnMobile = props?.visibility?.showOnMobile ?? true

  useRouterChanged(({ meta: { isPathChanged } }) => {
    if (isPathChanged && isOpen) {
      closeAihub()
    }
  })

  useEffect(() => {
    if (!searchParams || !pathname) {
      return
    }

    const aihubOpen = searchParams.get('aihubOpen')
    const activeTab = searchParams.get('aihubActiveTab')

    if (!aihubOpen && !activeTab) {
      return
    }

    // A deep link (e.g. a push-notification click) must not open a hub that is hidden on
    // this device — there would be no trigger to close it. The params are still stripped.
    const matches = (breakpoint?: string) =>
      !!breakpoint && window.matchMedia(`(min-width: ${breakpoint})`).matches
    const isVisibleHere = matches(desktopBreakpoint)
      ? showOnDesktop
      : matches(tabletBreakpoint)
        ? showOnTablet
        : showOnMobile

    if (isVisibleHere) {
      if (aihubOpen === 'true') {
        openAihub()
      }

      if (activeTab) {
        setActiveTabId(activeTab as AihubTabId)
      }
    }

    const newSearchParams = new URLSearchParams(searchParams.toString())

    newSearchParams.delete('aihubOpen')
    newSearchParams.delete('aihubActiveTab')

    router.replace(createUrl(pathname, newSearchParams))
  }, [
    desktopBreakpoint,
    openAihub,
    pathname,
    router,
    searchParams,
    setActiveTabId,
    showOnDesktop,
    showOnMobile,
    showOnTablet,
    tabletBreakpoint
  ])

  const { newAiOffers } = useAihubNotifications(props)

  return {
    newAiOffers
  }
}
